import type {
  ResourceType,
  SupportedFormat,
  ResourceDataType
} from './type';

/**
 * 파일 경로에서 파일 형식을 추출합니다.
 */
export function getFileFormat(path: string): SupportedFormat | null {
  // URL에서 쿼리 스트링 제거 (예: file.glb? -> file.glb)
  const cleanPath = path.split('?')[0];
  const extension = cleanPath.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'glb':
      return 'glb';
    case 'gltf':
      return 'gltf';
    case 'fbx':
      return 'fbx';
    case 'vrm':
      return 'vrm';
    case 'vrma':
      return 'vrma';
    default:
      return null;
  }
}

// /**
//  * 리소스 타입을 기반으로 기본 데이터 타입을 결정합니다.
//  */
// export function getDefaultResourceDataType(format: SupportedFormat): ResourceDataType {
//   switch (format) {
//     case 'gltf':
//     case 'glb':
//       return 'model_only';
//     case 'fbx':
//       return 'model_only';
//     case 'vrm':
//       return 'model_only';
//     default:
//       return 'model_only';
//   }
// }

/**
 * 진행률을 계산합니다.
 */
export function calculateProgress(loaded: number, total: number): number {
  return total > 0 ? Math.round((loaded / total) * 100) : 0;
}

/**
 * 전체 진행률을 계산합니다.
 */
export function calculateTotalProgress(loadedCount: number, totalCount: number): number {
  return totalCount > 0 ? Math.round((loadedCount / totalCount) * 100) : 0;
}

/**
 * 리소스 타입이 혼합 타입인지 확인합니다.
 */
export function isMixedResourceType(type: ResourceDataType): boolean {
  return type.includes('mixed');
}

/**
 * 애니메이션 추출이 필요한지 확인합니다.
 */
export function shouldExtractAnimations(resource: ResourceType): boolean {
  return resource.extractAnimations ||
    resource.type.includes('animation') ||
    resource.type.includes('mixed');
}

/**
 * 모델 추출이 필요한지 확인합니다.
 */
export function shouldExtractModel(resource: ResourceType): boolean {
  return resource.extractModel !== false &&
    (resource.type.includes('model') ||
      resource.type.includes('mixed'));
}

/**
 * 지원되는 파일 형식인지 확인합니다.
 */
export function isSupportedFormat(format: string): format is SupportedFormat {
  return ['glb', 'gltf', 'fbx', 'vrm'].includes(format);
}

/**
 * 파일 경로가 유효한지 확인합니다.
 */
export function isValidPath(path: string): boolean {
  return typeof path === 'string' && path.trim().length > 0;
}

/**
 * 리소스 이름이 유효한지 확인합니다.
 */
export function isValidResourceName(name: string): boolean {
  return typeof name === 'string' && name.trim().length > 0;
}

/**
 * 리소스 타입이 유효한지 확인합니다.
 */
export function validateResourceType(resource: ResourceType): void {
  if (!isValidResourceName(resource.name)) {
    throw new Error(`Invalid resource name: ${resource.name}`);
  }

  if (!isValidPath(resource.path)) {
    throw new Error(`Invalid resource path: ${resource.path}`);
  }

  const format = getFileFormat(resource.path);
  if (!format) {
    throw new Error(`Unsupported file format: ${resource.path}`);
  }

  if (resource.format !== format) {
    throw new Error(`Format mismatch: expected ${format}, got ${resource.format}`);
  }
}

/**
 * 여러 리소스를 검증합니다.
 */
export function validateResources(resources: ResourceType[]): void {
  resources.forEach(validateResourceType);

  // 중복 이름 검사
  const names = resources.map(r => r.name);
  const duplicates = names.filter((name, index) => names.indexOf(name) !== index);

  if (duplicates.length > 0) {
    throw new Error(`Duplicate resource names found: ${duplicates.join(', ')}`);
  }
}

export const determineResourceType = (
  url: string,
  options?: {
    hasAnimations?: boolean;
    hasModel?: boolean;
  }
): ResourceDataType => {
  // 옵션이 제공된 경우 더 정확한 타입 결정
  if (options) {
    const { hasAnimations, hasModel } = options;

    if (hasAnimations && hasModel) {
      return 'model_animation_both';
    } else if (hasAnimations) {
      return 'animation_only';
    } else if (hasModel) {
      return 'model_only';
    }
  }

  // 기본값: 모델만 포함
  return 'model_only';
};
